import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

const CustomSelect = ({ value, onChange, options, placeholder, id, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownDirection, setDropdownDirection] = useState('down');
  const selectRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const calculateDropdownDirection = () => {
    if (selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = Math.min(options.length * 40, 192); // max-h-48 = 192px
      
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownDirection('up');
      } else {
        setDropdownDirection('down');
      }
    }
  };

  const handleSelect = (optionValue) => {
    if (disabled) return;
    onChange({ target: { value: optionValue } });
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleToggleOpen = () => {
    if (disabled) return;
    if (!isOpen) {
      calculateDropdownDirection();
    }
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        calculateDropdownDirection();
        setIsOpen(true);
        setHighlightedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(options[highlightedIndex].value);
        }
        break;
    }
  };

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className="relative" ref={selectRef}>
      <div
        tabIndex={disabled ? -1 : 0}
        onClick={handleToggleOpen}
        onKeyDown={handleKeyDown}
        className={`w-full p-2.5 text-sm text-slate-700 border border-gray-300 rounded-md flex items-center justify-between transition-all duration-200 ${
          disabled 
            ? 'bg-gray-100 cursor-not-allowed text-gray-600' 
            : 'bg-white cursor-pointer hover:border-slate-400 focus:outline-none'
        } ${
          isOpen && !disabled ? 'border-slate-500 ring-2 ring-slate-100' : ''
        }`}
      >
        <span className={selectedOption ? (disabled ? 'text-gray-600' : 'text-slate-700') : 'text-slate-400'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown 
          size={16} 
          className={`text-slate-500 transition-transform duration-200 ${isOpen && !disabled ? 'rotate-180' : ''} ${disabled ? 'text-gray-400' : ''}`} 
        />
      </div>
      
      {isOpen && !disabled && (
        <div 
          className={`absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto ${
            dropdownDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {options.map((option, index) => (
            <div
              key={option.value}
              onClick={() => handleSelect(option.value)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-3 py-2 text-xs cursor-pointer transition-colors duration-150 ${
                highlightedIndex === index || value === option.value
                  ? 'bg-slate-100 text-slate-800' 
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-700'
              }`}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AccountRegionStep({ 
  dashboardData, 
  handleDashboardDataChange, 
  regions, 
  awsAccounts,
  isEditingDashboard 
}) {
  // Filter accounts to only show active ones, similar to Filters.jsx
  const activeAccounts = awsAccounts.filter(account => account.Status === 'active');
  
  const accountOptions = activeAccounts.map(account => ({
    value: account.AccountNo,
    label: `${account.AccountName} (${account.AccountNo})`
  }));

  const regionOptions = regions.map(region => ({
    value: region.name,
    label: region.description
  }));

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="bg-[#003366]/10 p-4 rounded-lg mb-6 border border-[#003366]/20">
        <p className="text-[#004488]">
          Set up the AWS account and region for your dashboard.
          {isEditingDashboard && (
            <span className="block text-gray-600 text-sm mt-1">
              Account and region cannot be modified when editing.
            </span>
          )}
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label
            htmlFor="accountNo"
            className="block text-sm font-medium text-slate-700 mb-2"
          >
            AWS Account *
            {isEditingDashboard && (
              <span className="text-gray-500 text-xs ml-2"></span>
            )}
          </label>
          <CustomSelect
            id="accountNo"
            value={dashboardData.accountNo}
            onChange={(e) => handleDashboardDataChange("accountNo", e.target.value)}
            options={accountOptions}
            placeholder="Select Account"
            disabled={isEditingDashboard}
          />
        </div>

        <div>
          <label
            htmlFor="region"
            className="block text-sm font-medium text-slate-700 mb-2"
          >
            AWS Region *
            {isEditingDashboard && (
              <span className="text-gray-500 text-xs ml-2"></span>
            )}
          </label>
          <CustomSelect
            id="region"
            value={dashboardData.region}
            onChange={(e) => handleDashboardDataChange("region", e.target.value)}
            options={regionOptions}
            placeholder="Select Region"
            disabled={isEditingDashboard}
          />
        </div>
      </div>
    </div>
  );
}